import { Button } from "@mui/material";
import TextField from "@mui/material/TextField";
import ShareIcon from "@mui/icons-material/Share";
import EditIcon from "@mui/icons-material/Edit";
import copy from "copy-to-clipboard";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  useAccount,
  useContractRead,
  useContractWrite,
  useWaitForTransaction,
} from "wagmi";
import { Chains, SpaceInviteCodeABI, SpaceSetInviteCodeABI } from "../data";
import { useAppSelector } from "../../app/hooks";
import { selectChain } from "../../features/chain";
import { emptyEthAddress } from "../../utils/util";
import { getJicAddress, onJicChange } from "../../lib/jic";
import {
  publishJicSiteInviteCode,
  resolveJicReferralInput,
} from "../../lib/jicReferral";
import {
  getInjectedWalletAddress,
  hasInjectedProvider,
  sendInjectedContractTransaction,
  waitForInjectedReceipt,
} from "../../lib/injectedWallet";

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T) {
  return new Promise<T>((resolve) => {
    const timer = window.setTimeout(() => resolve(fallback), ms);
    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        window.clearTimeout(timer);
        resolve(fallback);
      });
  });
}

const MyInvitation = () => {
  const [editing, setEditing] = useState(false);
  const chain = useAppSelector(selectChain);
  const { address: signerAddress } = useAccount();
  const [jicAddress, setJicAddress] = useState<string | null>(() => getJicAddress());
  const [injectedAddress, setInjectedAddress] = useState<string | null>(null);
  const address =
    jicAddress ||
    (signerAddress ? signerAddress.toLowerCase() : null) ||
    injectedAddress;
  const [processing, setProcessing] = useState(false);
  const [code, setCode] = useState("");

  useEffect(
    () =>
      onJicChange((user) => {
        setJicAddress(user?.sub ? user.sub.toLowerCase() : null);
      }),
    []
  );

  useEffect(() => {
    let live = true;
    const refreshInjectedAddress = async () => {
      if (!hasInjectedProvider()) {
        if (live) setInjectedAddress(null);
        return;
      }
      const walletAddress = await getInjectedWalletAddress({
        requestIfNeeded: false,
      }).catch(() => null);
      if (live) setInjectedAddress(walletAddress ? walletAddress.toLowerCase() : null);
    };
    refreshInjectedAddress();
    window.addEventListener("focus", refreshInjectedAddress);
    return () => {
      live = false;
      window.removeEventListener("focus", refreshInjectedAddress);
    };
  }, []);

  const { refetch: fetchInviteCode } = useContractRead({
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: SpaceInviteCodeABI,
    functionName: "inviteCode",
    args: [(address ?? emptyEthAddress) as `0x${string}`],
    enabled: false,
  });

  const refreshInviteCode = async () => {
    if (!address) {
      setCode("");
      return;
    }

    const chainCodePromise = fetchInviteCode()
      .then((res) =>
        typeof res.data === "string" ? res.data.trim().toLowerCase() : ""
      )
      .catch(() => "");

    const fallbackCode = await withTimeout(
      resolveJicReferralInput("face", address),
      900,
      ""
    );
    if (fallbackCode) setCode(fallbackCode);

    const chainCode = await withTimeout(chainCodePromise, 1500, "");
    if (chainCode) {
      setCode(chainCode);
    } else if (!fallbackCode) {
      setCode("");
    }
  };

  useEffect(() => {
    if (address) {
      refreshInviteCode();
    } else {
      setCode("");
    }
    // eslint-disable-next-line
  }, [address, chain]);

  const onSetCode = () => {
    setEditing((editing) => !editing);
  };
  const onCodeChange = (e: any) => {
    if (e.target.value.length <= 128) {
      setCode(e.target.value.trim().toLowerCase());
    } else {
      toast.error("Invitation code too long");
    }
  };

  const handleSetInviteError = (error: Error) => {
    var simpleMsg = error.message.split(".")[0];
    if (error.message.includes("This invite code has been used")) {
      simpleMsg =
        "This invite code has been used, please try another invite code";
    }
    toast.error(simpleMsg);
    setProcessing(false);
    refreshInviteCode();
  };

  const { data: setInviteCodeRes, write: setInviteCode } = useContractWrite({
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: SpaceSetInviteCodeABI,
    functionName: "setInviteCode",
    args: [code],
    onError: handleSetInviteError,
  });

  useWaitForTransaction({
    hash: setInviteCodeRes?.hash,
    onSuccess() {
      toast.success("Set invitation code succeeded!");
      setProcessing(false);
      refreshInviteCode();
      publishJicSiteInviteCode("face", code).then((r) => {
        if (!r.ok && r.reason) toast.error(r.reason);
      });
    },
    onError(err) {
      var simpleMsg = "Set invitation code failed!" + err.message.split(".")[0];
      toast.error(simpleMsg);
      setProcessing(false);
      refreshInviteCode();
    },
  });

  const onConfirmCode = async () => {
    if (!code || code.trim() === "") {
      toast.error("Invitation code cannot be empty");
      return;
    }
    setEditing(false);
    setProcessing(true);
    if (signerAddress && setInviteCode) {
      setInviteCode?.();
      return;
    }

    try {
      const txHash = await sendInjectedContractTransaction({
        chainId: Chains[chain].chainId,
        chainName: Chains[chain].chainName,
        rpcUrl: Chains[chain].provider,
        to: Chains[chain].contractAddr as `0x${string}`,
        abi: SpaceSetInviteCodeABI,
        functionName: "setInviteCode",
        args: [code],
      });
      await waitForInjectedReceipt(txHash, {
        rpcUrl: Chains[chain].provider,
      });
      toast.success("Set invitation code succeeded!");
      setProcessing(false);
      refreshInviteCode();
      publishJicSiteInviteCode("face", code).then((r) => {
        if (!r.ok && r.reason) toast.error(r.reason);
      });
    } catch (error) {
      handleSetInviteError(
        error instanceof Error ? error : new Error("Set invitation code failed")
      );
    }
  };
  return (
    <div className="w-full flex flex-col">
      <p className="p-4 font-Armstrong text-lg md:text-xl text-gray-300">
        My Invitation code
      </p>

      <div
        className="select-none w-[303px] h-[171px] flex gap-1 px-4  items-center  justify-center bg-fit rounded-lg"
        style={{
          backgroundImage: `url("/assets/ticket.png")`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <TextField
          className="w-48"
          placeholder=""
          label="invitation code"
          onChange={onCodeChange}
          size="small"
          value={code}
          slotProps={{
            input: {
              readOnly: !editing,
            },
          }}
        />
        {processing ? (
          "Processing"
        ) : (
          <Button onClick={editing ? onConfirmCode : onSetCode}>
            {editing ? "Confirm" : <EditIcon />}
          </Button>
        )}
        <Button
          className=""
          onClick={() => {
            if (code) {
              const invitationLink = `${window.location.origin}?ref=${code}`;
              copy(invitationLink);
              toast.success("Invitation link copied to clipboard");
            } else {
              toast.error("Invitation code empty");
            }
          }}
        >
          <ShareIcon />
        </Button>
      </div>
    </div>
  );
};
export default MyInvitation;
